import { FormEvent, useMemo, useState } from "react";
import { posthog } from '../../../lib/posthog';
import { buildPublicAppUrl } from "../../../services/apiUrl";
import { resendVerificationEmail, verifyEmailToken } from "../api/adminAuthApi";
import { AuthField } from "../components/AuthField";
import { AuthShell } from "../components/AuthShell";
import { AuthStatusMessage } from "../components/AuthStatusMessage";
import { AuthSubmitButton } from "../components/AuthSubmitButton";
import { navigateTo } from "../components/authNavigation";

function getVerifyEmailUrl(email: string, flow: string): string {
  const path = `/admin/verify-email?email=${encodeURIComponent(email)}&flow=${encodeURIComponent(flow)}`;
  return buildPublicAppUrl(path);
}

function getVerificationContext() {
  if (typeof window === "undefined") {
    return {
      email: "",
      flow: "sign-in",
    };
  }

  const params = new URLSearchParams(window.location.search);
  return {
    email: params.get("email") ?? "",
    flow: params.get("flow") ?? "sign-in",
  };
}

function getVerificationTitle(flow: string): string {
  return flow === "sign-up"
    ? "Verify Your Workspace Email"
    : "Verify Your Workspace Sign In";
}

function getVerificationSubtitle(flow: string, email: string): string {
  const target = email
    ? `We sent a verification link to ${email}.`
    : "We sent a verification link to your email.";

  if (flow === "sign-up") {
    return `${target} If you need to paste the verification token manually, enter it below to finish setting up your account and workspace access.`;
  }

  return `${target} If you need to paste the verification token manually, enter it below to continue signing in to your workspace.`;
}

function getFriendlyVerificationError(
  error: { code?: string | null; message?: string } | null | undefined,
): string {
  if (!error) {
    return "Unable to verify your email right now.";
  }

  if (
    error.code === "INVALID_TOKEN" ||
    error.code === "TOKEN_EXPIRED" ||
    /invalid token|expired/i.test(error.message ?? "")
  ) {
    return "This verification token is expired or no longer valid. Request a new verification email to continue.";
  }

  return error.message ?? "Unable to verify your email right now.";
}

export function VerifyEmailPage() {
  const [{ email, flow }] = useState(() => getVerificationContext());
  const [verificationToken, setVerificationToken] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const normalizedToken = useMemo(
    () => verificationToken.trim(),
    [verificationToken],
  );

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (!normalizedToken) {
      setError("Enter the verification token to continue.");
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await verifyEmailToken({
        token: normalizedToken,
      });

      if (!result.ok) {
        setError(getFriendlyVerificationError(result.error));
        return;
      }

      setSuccess("Email verified. Redirecting to your workspace...");
      posthog.capture('auth_email_verified', { email, flow });
      navigateTo("/admin");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleResendVerificationEmail() {
    if (!email) {
      setError(
        "Return to sign in or sign up so we know where to send the next verification email.",
      );
      return;
    }

    setError(null);
    setSuccess(null);
    setIsResending(true);

    try {
      const result = await resendVerificationEmail({
        email,
        callbackUrl: getVerifyEmailUrl(email, flow),
      });

      if (!result.ok) {
        setError(result.error.message);
        return;
      }

      setSuccess("A new verification email has been sent to your inbox.");
    } finally {
      setIsResending(false);
    }
  }

  return (
    <AuthShell
      title={getVerificationTitle(flow)}
      subtitle={getVerificationSubtitle(flow, email)}
      badgeIcon="mark_email_read"
      badgeLabel="Metaphysical Labs"
      data-oid="s5u--r4"
    >
      <form className="auth-form" onSubmit={handleSubmit} data-oid="d9-doz9">
        <div className="auth-verification" data-oid="kz64ahp">
          <AuthField
            label="Verification Token"
            name="verificationToken"
            type="text"
            autoComplete="one-time-code"
            inputMode="text"
            placeholder="Enter your token"
            leadingIcon="mail_lock"
            value={verificationToken}
            onChange={(event) => {
              setVerificationToken(event.target.value);
              setError(null);
              setSuccess(null);
            }}
            hint="Click the verification link in your email first. If you need to paste the token manually, use the full token from that email here."
            data-oid="papt._u"
          />
        </div>

        {error ? (
          <AuthStatusMessage tone="error" message={error} data-oid="yxb83va" />
        ) : null}
        {success ? (
          <AuthStatusMessage
            tone="success"
            message={success}
            data-oid="x1feorm"
          />
        ) : null}
        <AuthSubmitButton
          type="submit"
          isBusy={isSubmitting}
          busyLabel="Verifying Email..."
          data-oid="8io5.e4"
        >
          Verify Email
        </AuthSubmitButton>
      </form>

      <div className="auth-footer auth-footer--stacked" data-oid=":9p0u.3">
        <p className="auth-footer-copy" data-oid="dej4eaj">
          Didn't receive the email?{" "}
          <button
            className="auth-link-button"
            type="button"
            onClick={() => void handleResendVerificationEmail()}
            disabled={isResending}
            data-oid="6avr0bw"
          >
            {isResending ? "Resending..." : "Resend Verification Email"}
          </button>
        </p>
        <p className="auth-footer-copy" data-oid="jyg0695">
          Need to use a different email?{" "}
          <button
            className="auth-link-button"
            type="button"
            onClick={() =>
              navigateTo(
                flow === "sign-up" ? "/admin/sign-up" : "/admin/sign-in",
              )
            }
            data-oid="wgsv-mk"
          >
            Go Back
          </button>
        </p>
      </div>
    </AuthShell>
  );
}
